import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

export function getHeadingLevel(attrs: { level?: unknown }): number {
  if (typeof attrs.level === 'number' && Number.isFinite(attrs.level)) {
    return attrs.level;
  }
  const parsed = Number(attrs.level);
  return Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1;
}

function getContentEndBeforeFootnotes(doc: ProseMirrorNode): number {
  let offset = 0;
  for (let index = 0; index < doc.childCount; index += 1) {
    const child = doc.child(index);
    if (child.type.name === 'footnotes') {
      return offset;
    }
    offset += child.nodeSize;
  }
  return doc.content.size;
}

export function findSectionEndPos(
  doc: ProseMirrorNode,
  startPos: number,
  startLevel: number,
): number {
  let endPos = getContentEndBeforeFootnotes(doc);
  let boundaryFound = false;
  doc.descendants((node, pos) => {
    if (boundaryFound || pos <= startPos) {
      return !boundaryFound;
    }
    if (node.type.name === 'footnotes') {
      endPos = Math.min(endPos, pos);
      boundaryFound = true;
      return false;
    }
    if (node.type.name === 'heading' && getHeadingLevel(node.attrs) <= startLevel) {
      endPos = pos;
      boundaryFound = true;
      return false;
    }
    return true;
  });
  return endPos;
}

export function resolveHeadingPos(
  doc: ProseMirrorNode,
  hintPos: number,
  level: number,
  text: string,
): number | null {
  const direct = doc.nodeAt(hintPos);
  if (
    direct?.type.name === 'heading'
    && getHeadingLevel(direct.attrs) === level
    && direct.textContent === text
  ) {
    return hintPos;
  }

  try {
    const resolved = doc.resolve(Math.min(Math.max(hintPos, 0), doc.content.size));
    for (let depth = resolved.depth; depth > 0; depth -= 1) {
      const candidate = resolved.node(depth);
      if (
        candidate.type.name === 'heading'
        && getHeadingLevel(candidate.attrs) === level
        && candidate.textContent === text
      ) {
        return resolved.before(depth);
      }
    }
  } catch {
  }

  const candidates: number[] = [];
  doc.descendants((node, pos) => {
    if (
      node.type.name === 'heading'
      && getHeadingLevel(node.attrs) === level
      && node.textContent === text
    ) {
      candidates.push(pos);
    }
    return true;
  });

  if (candidates.length === 0) {
    return null;
  }
  if (candidates.length === 1) {
    return candidates[0];
  }

  return candidates.reduce((best, pos) => (
    Math.abs(pos - hintPos) < Math.abs(best - hintPos) ? pos : best
  ));
}

export function getSectionRange(
  doc: ProseMirrorNode,
  headingPos: number,
  levelHint?: number,
): { from: number; to: number } | null {
  const node = doc.nodeAt(headingPos);
  if (!node || node.type.name !== 'heading') {
    return null;
  }
  const level = levelHint ?? getHeadingLevel(node.attrs);
  return {
    from: headingPos,
    to: findSectionEndPos(doc, headingPos, level),
  };
}

export function getSectionRangeForHeading(
  doc: ProseMirrorNode,
  hintPos: number,
  level: number,
  text: string,
): { from: number; to: number } | null {
  const headingPos = resolveHeadingPos(doc, hintPos, level, text);
  if (headingPos === null) {
    return null;
  }
  return {
    from: headingPos,
    to: findSectionEndPos(doc, headingPos, level),
  };
}
